import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { TokenStorageService } from '@services/token-storage.service';
import {
  CHATROOM_LAST_READ_PREFIX,
  getSessionUserEmail,
  nowTaipeiIso,
  pickLatestTimestamp,
} from './chat-read-state.util';

interface PlaceChatroomSummary {
  lastMessageAt: string;
  lastSenderMail: string;
}

/**
 * 場域聊天室未讀狀態服務（全域單例）。
 *
 * 後端 `/team_app/{appId}/places/chatroom/unread-summary` 回傳各場域最新訊息時間與發送者；
 * 「已讀時間」存在 localStorage（依使用者 email 命名空間），
 * 以「最新訊息時間 > 已讀時間 且 最新訊息非本人發送」判定未讀。
 *
 * 採用輪詢（聊天室未啟用 WebSocket），元件以 register() 登記要追蹤的 app，
 * 並訂閱 unreadPlaces$ 顯示紅點；進入聊天室時呼叫 markRead() 清除。
 */
@Injectable({ providedIn: 'root' })
export class ChatroomUnreadService {
  private http = inject(HttpClient);

  private readonly POLL_MS = 45000;
  private readonly summaries = new Map<string, PlaceChatroomSummary>();
  private readonly registeredApps = new Set<number>();
  private currentTeam = '';
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  private readonly unreadSet$ = new BehaviorSubject<Set<string>>(new Set<string>());
  readonly unreadPlaces$: Observable<Set<string>> = this.unreadSet$.asObservable();

  get hasAnyUnread(): boolean {
    return this.unreadSet$.value.size > 0;
  }

  isPlaceUnread(placesId: string | null | undefined): boolean {
    return !!placesId && this.unreadSet$.value.has(placesId);
  }

  /** 登記要追蹤的團隊與 app；切換團隊時清空舊狀態。 */
  register(teamCode: string, appId: number): void {
    if (!teamCode) {
      return;
    }
    if (teamCode !== this.currentTeam) {
      this.currentTeam = teamCode;
      this.registeredApps.clear();
      this.summaries.clear();
      this.unreadSet$.next(new Set<string>());
    }
    this.registeredApps.add(appId);
    this.ensureTimer();
    this.refresh();
  }

  /** 進入聊天室或讀取完訊息時呼叫，將該場域標記為已讀。 */
  markRead(placesId: string, at?: string): void {
    if (!placesId) {
      return;
    }
    const summary = this.summaries.get(placesId);
    const next = pickLatestTimestamp(
      at,
      nowTaipeiIso(),
      summary?.lastMessageAt,
      this.getLastRead(placesId),
    );
    if (next) {
      try {
        localStorage.setItem(this.lastReadKey(placesId), next);
        this.removeLegacyLastReadKey(placesId);
      } catch {
        // localStorage 不可用時忽略
      }
    }
    this.recompute();
  }

  refresh(): void {
    if (!this.currentTeam || this.registeredApps.size === 0) {
      return;
    }
    const token = TokenStorageService.getToken();
    if (!token) {
      return;
    }
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    this.registeredApps.forEach((appId) => {
      this.http
        .post<any>(
          `${environment.apiBaseUrl}/team_app/${appId}/places/chatroom/unread-summary`,
          { team_code: this.currentTeam },
          { headers },
        )
        .subscribe({
          next: (res) => {
            if (res?.token) {
              TokenStorageService.refreshToken(res.token);
            }
            const list: any[] = res?.data || [];
            for (const item of list) {
              if (!item?.places_id) {
                continue;
              }
              this.summaries.set(item.places_id, {
                lastMessageAt: item.last_message_at || '',
                lastSenderMail: (item.last_sender_mail || '').trim().toLowerCase(),
              });
            }
            this.recompute();
          },
          error: () => {
            // 該 app 無權限或請求失敗時忽略，不影響其他 app
          },
        });
    });
  }

  private ensureTimer(): void {
    if (this.pollTimer || typeof window === 'undefined') {
      return;
    }
    this.pollTimer = setInterval(() => this.refresh(), this.POLL_MS);
  }

  private recompute(): void {
    const me = getSessionUserEmail();
    const set = new Set<string>();
    this.summaries.forEach((summary, placesId) => {
      if (!summary.lastMessageAt) {
        return;
      }
      if (summary.lastSenderMail && summary.lastSenderMail === me) {
        return;
      }
      if (summary.lastMessageAt > this.getLastRead(placesId)) {
        set.add(placesId);
      }
    });
    this.unreadSet$.next(set);
  }

  private lastReadKey(placesId: string): string {
    return `${CHATROOM_LAST_READ_PREFIX}${getSessionUserEmail()}:${placesId}`;
  }

  private legacyLastReadKey(placesId: string): string {
    return `${CHATROOM_LAST_READ_PREFIX}:${placesId}`;
  }

  private getLastRead(placesId: string): string {
    try {
      const key = this.lastReadKey(placesId);
      const current = localStorage.getItem(key);
      if (current) {
        return current;
      }
      const legacy = localStorage.getItem(this.legacyLastReadKey(placesId));
      if (legacy && getSessionUserEmail()) {
        localStorage.setItem(key, legacy);
        localStorage.removeItem(this.legacyLastReadKey(placesId));
        return legacy;
      }
      return legacy || '';
    } catch {
      return '';
    }
  }

  private removeLegacyLastReadKey(placesId: string): void {
    try {
      localStorage.removeItem(this.legacyLastReadKey(placesId));
    } catch {
      /* ignore */
    }
  }
}
